import React, {
  useEffect,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import { ContractTreeAPI } from "../Services/Api";
  
import { useSession } from "../Contexts/SessionContext";

type AddSectionProps = {
  onSaved?: () => void;

  /*
    null = root section

    number = child of this node
  */
  parentNode?: number | null;
};

const AddSection: React.FC<
  AddSectionProps
> = ({
  onSaved,
  parentNode = null,
}) => {
  // =========================================================
  // CURRENT TENDER
  // =========================================================

  const { tenderNo } = useParams<{
    tenderNo: string;
  }>();

  const tenderNumber =
    Number(tenderNo);

  // Current project/tender session created from the dashboard.
  const { sessionNo } = useSession();

  // =========================================================
  // FORM
  // =========================================================

  const [
    contractNode,
    setContractNode,
  ] = useState("");

  const [
  sectionNo,
  setSectionNo,
] = useState("");


  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  // =========================================================
  // CHILD?
  // =========================================================

  const isChild =
    parentNode !== null &&
    parentNode !== undefined;

  // =========================================================
  // RESET FORM WHEN PARENT CHANGES
  // =========================================================

  useEffect(() => {
    setContractNode("");
     setSectionNo("");
    setMessage("");
    setError("");
  }, [parentNode]);

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    setMessage("");
    setError("");

    // -------------------------------------------------------
    // VALIDATE NAME
    // -------------------------------------------------------

    if (
      !contractNode.trim()
    ) {
      setError(
        "Section name is required."
      );

      return;
    }

    // -------------------------------------------------------
    // VALIDATE TENDER
    // -------------------------------------------------------

    if (
      !Number.isInteger(
        tenderNumber
      ) ||
      tenderNumber <= 0
    ) {
      setError(
        "Invalid Tender number."
      );

      return;
    }

    // -------------------------------------------------------
    // VALIDATE SESSION
    // -------------------------------------------------------

    const currentSessionNo = Number(sessionNo);

    if (
      !Number.isInteger(currentSessionNo) ||
      currentSessionNo <= 0
    ) {
      setError(
        "Session not found. Please select the project again."
      );
      return;
    }

    try {
      setSaving(true);

      // =====================================================
      // ROOT:
      // parentNode = 0
      //
      // CHILD:
      // parentNode = selected node's ContractNodeNo
      // =====================================================

      const data = {
        contractNode: contractNode.trim(),
        sectionNo: sectionNo.trim() || null,
        contract: tenderNumber,
        parentNode: parentNode ?? 0,
        workNode: 0,
        sessionNo: currentSessionNo,
      };

      // -----------------------------------------------------
      // DEBUG
      // -----------------------------------------------------

      console.log(
        "ADDING CONTRACT SECTION:",
        data
      );

      // -----------------------------------------------------
      // SAVE
      // -----------------------------------------------------

      const result =
        await ContractTreeAPI.addSection(
          data
        );

      console.log(
        "ADD SECTION RESPONSE:",
        result
      );

      // -----------------------------------------------------
      // SUCCESS
      // -----------------------------------------------------

      setMessage(
        `Section "${result.data.contractNode}" added successfully.`
      );

      setContractNode("");
      setSectionNo("");

      // -----------------------------------------------------
      // REFRESH TREE
      // -----------------------------------------------------

      if (onSaved) {
        onSaved();
      }
    } catch (err: any) {
      console.error(
        "Add Section Error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to add section."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <div
      className="
        w-full
        bg-white
      "
    >
      <div
        className="
          p-5
        "
      >
        {/* ===================================================
            HEADER
           =================================================== */}

        <div className="mb-5">
          <h2
            className="
              text-[17px]
              font-semibold
              text-[#53538c]
            "
          >
            {isChild
              ? "Add Sub Section"
              : "Add Section"}
          </h2>

          <p
            className="
              text-[11px]
              text-gray-400
              mt-1
            "
          >
            {isChild
              ? "Create a section under the selected Contract Tree node."
              : "Create a new main Contract Tree section."}
          </p>
        </div>

        {/* ===================================================
            PARENT
           =================================================== */}

        {isChild && (
          <div
            className="
              mb-5
              px-3
              py-3
              rounded-lg
              bg-[#f1f6ff]
              border
              border-[#d3e3fd]
            "
          >
            <div
              className="
                text-[10px]
                uppercase
                tracking-wide
                text-[#5f6368]
              "
            >
              Parent Contract Node
            </div>

            <div
              className="
                mt-1
                text-[13px]
                font-semibold
                text-[#174ea6]
              "
            >
              #{parentNode}
            </div>

            <div
              className="
                mt-1
                text-[10px]
                text-[#80868b]
              "
            >
              New section will be created
              under this node.
            </div>
          </div>
        )}

        {/* ===================================================
            SUCCESS
           =================================================== */}

        {message && (
          <div
            className="
              mb-4
              px-3
              py-2.5
              rounded-lg
              border
              border-green-200
              bg-green-50
              text-green-700
              text-[12px]
            "
          >
            {message}
          </div>
        )}

        {/* ===================================================
            ERROR
           =================================================== */}

        {error && (
          <div
            className="
              mb-4
              px-3
              py-2.5
              rounded-lg
              border
              border-red-200
              bg-red-50
              text-red-600
              text-[12px]
            "
          >
            {error}
          </div>
        )}

        {/* ===================================================
            FORM
           =================================================== */}

        <form
          onSubmit={
            handleSave
          }
          className="
            space-y-5
          "
        >

          {/* =================================================
    SECTION NO
   ================================================= */}

<div>
  <label
    className="
      block
      mb-2
      text-[12px]
      font-medium
      text-gray-700
    "
  >
    Section No
  </label>

  <input
    type="text"
    value={sectionNo}
    onChange={(e) =>
      setSectionNo(e.target.value)
    }
    maxLength={50}
    placeholder="Enter section no"
    className="
      w-full
      h-[40px]
      px-3
      rounded-lg
      border
      border-gray-300
      text-[13px]
      outline-none
      focus:border-[#53538c]
      focus:ring-1
      focus:ring-[#53538c]
    "
  />
</div>
          {/* =================================================
              SECTION NAME
             ================================================= */}

          <div>
            <label
              className="
                block
                mb-2
                text-[12px]
                font-medium
                text-gray-700
              "
            >
              Section Name

              <span
                className="
                  text-red-500
                  ml-1
                "
              >
                *
              </span>
            </label>

            <input
              type="text"
              value={
                contractNode
              }
              onChange={e =>
                setContractNode(
                  e.target.value
                )
              }
              maxLength={50}
              autoFocus
              placeholder={
                isChild
                  ? "Enter sub section name"
                  : "Enter section name"
              }
              className="
                w-full
                h-[40px]
                px-3
                rounded-lg
                border
                border-gray-300
                text-[13px]
                outline-none
                focus:border-[#53538c]
                focus:ring-1
                focus:ring-[#53538c]
              "
            />
          </div>

          {/* =================================================
              SAVE
             ================================================= */}

          <button
            type="submit"
            disabled={saving}
            className="
              w-full
              h-[42px]
              rounded-lg
              bg-[#53538c]
              hover:bg-[#454575]
              text-white
              text-[12px]
              font-medium
              transition
              disabled:opacity-60
              disabled:cursor-not-allowed
            "
          >
            {saving
              ? "Saving..."
              : isChild
              ? "Save Sub Section"
              : "Save Section"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddSection;